import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, Pencil, Plus, RotateCcw, Search, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import JaceLayout from "./JaceLayout";
import JaceAthleteForm from "./JaceAthleteForm";
import { JACE } from "./shared";

export default function JaceAthletes() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [archived, setArchived] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null); // null | {} | athlete
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (archived) params.set("archived", "true");
    api.get(`/jace/athletes?${params}`).then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  };
  useEffect(load, [archived]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleArchive = async (item, value) => {
    try {
      await api.post(`/jace/athletes/${item.id}/archive`, { archived: value });
      toast.success(value ? "Atleet gearchiveerd" : "Atleet hersteld");
      load();
    } catch { toast.error("Archiveren mislukt"); }
  };

  const doDelete = async (item) => {
    try {
      await api.delete(`/jace/athletes/${item.id}`);
      toast.success("Atleet definitief verwijderd");
      setConfirmDelete(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.message || "Verwijderen mislukt");
      setConfirmDelete(null);
    }
  };

  return (
    <JaceLayout>
      <div data-testid="jace-athletes-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="section-tag section-tag-dark mb-3">// ATHLETES — PROFIELEN</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl">Athletes.</h2>
            <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">Handmatig atletenbeheer met profielen, duplicaat-controle en optionele koppeling aan website-leden.</p>
          </div>
          <button onClick={() => setForm({})} className="chip px-4 py-2.5 inline-flex items-center gap-2" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-athletes-new-btn"><Plus size={13}/> NEW ATHLETE</button>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-56">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A4A4A]"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Zoek op naam, e-mail of telefoon…" className="editorial-input dark w-full !pl-9" data-testid="jace-athletes-search"/>
          </div>
          <button onClick={() => setArchived(!archived)} className="chip px-3 py-2 border transition-colors" style={archived ? { borderColor: JACE, color: JACE } : { borderColor: "#F5F3F033", color: "#A3A3A3" }} data-testid="jace-athletes-archived-toggle">
            {archived ? "ARCHIVE ACTIEF" : "TOON ARCHIVE"}
          </button>
          <button onClick={load} className="btn-ghost-dark" data-testid="jace-athletes-apply">ZOEK</button>
        </div>

        {items === null ? <div className="chip text-[#A3A3A3]">Laden…</div> : items.length === 0 ? (
          <div className="border border-[#F5F3F0]/15 p-10 text-center" data-testid="jace-athletes-empty">
            <Users size={26} className="mx-auto" style={{ color: JACE }}/>
            <div className="font-display uppercase text-2xl mt-4">Nog geen atleten.</div>
            <div className="chip text-[#4A4A4A] mt-2">Maak handmatig een atleet aan om feedback en sessies te koppelen.</div>
          </div>
        ) : (
          <div className="line-t line-dark" data-testid="jace-athletes-list">
            {items.map((item) => (
              <div key={item.id} className="line-b line-dark py-4 flex flex-wrap items-center gap-3" data-testid={`jace-athlete-row-${item.id}`}>
                <button onClick={() => nav(`/jace/athletes/${item.id}`)} className="flex-1 min-w-56 text-left">
                  <div className="font-display uppercase text-xl">{item.name}</div>
                  <div className="chip text-[#A3A3A3] mt-1">
                    {item.dob || "geen geboortedatum"}{item.email ? ` · ${item.email}` : ""}{item.phone ? ` · ${item.phone}` : ""}
                  </div>
                </button>
                {item.member_email && <span className="chip px-2 py-1 border" style={{ borderColor: JACE, color: JACE }} data-testid={`jace-athlete-linked-${item.id}`}>LID GEKOPPELD</span>}
                <button onClick={() => setForm(item)} className="chip px-3 py-2 border border-[#F5F3F0]/25 text-[#A3A3A3] hover:text-white inline-flex items-center gap-1.5" data-testid={`jace-athlete-edit-${item.id}`}><Pencil size={12}/> EDIT</button>
                <button onClick={() => toggleArchive(item, !archived)} className="chip px-3 py-2 border border-[#F5F3F0]/25 text-[#A3A3A3] hover:text-white inline-flex items-center gap-1.5" data-testid={archived ? `jace-athlete-restore-${item.id}` : `jace-athlete-archive-${item.id}`}>
                  {archived ? <><RotateCcw size={12}/> HERSTEL</> : <><Archive size={12}/> ARCHIVE</>}
                </button>
                <button onClick={() => setConfirmDelete(item)} className="chip px-3 py-2 border border-[#F87171]/40 text-[#F87171] inline-flex items-center gap-1.5" data-testid={`jace-athlete-delete-${item.id}`}><Trash2 size={12}/> DELETE</button>
              </div>
            ))}
          </div>
        )}

        {form !== null && (
          <JaceAthleteForm athlete={form.id ? form : null} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
        )}

        {confirmDelete && (
          <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
            <div className="bg-[#0A0A0A] border border-[#F87171]/40 max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="jace-athlete-delete-confirm">
              <div className="chip text-[#F87171] mb-3">// DEFINITIEF VERWIJDEREN</div>
              <p className="text-sm text-[#A3A3A3]">Atleet <span className="text-[#F5F3F0]">{confirmDelete.name}</span> wordt permanent verwijderd. Dit kan alleen als er geen gekoppelde sessies of feedback zijn.</p>
              <div className="flex justify-end gap-2 mt-6">
                <button onClick={() => setConfirmDelete(null)} className="btn-ghost-dark" data-testid="jace-athlete-delete-cancel">ANNULEREN</button>
                <button onClick={() => doDelete(confirmDelete)} className="chip px-4 py-2.5 bg-[#F87171] text-[#0A0A0A]" data-testid="jace-athlete-delete-confirm-btn">VERWIJDER</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </JaceLayout>
  );
}
